import { ActivityIcon } from "@/Icons/v2/SidebarIcons/Activity";
import { ContaminationIcon } from "@/Icons/v2/SidebarIcons/Contamination";
import { DrilldownIcon } from "@/Icons/v2/SidebarIcons/Drilldown";
import { EventsIcon } from "@/Icons/v2/SidebarIcons/Events";
import { HomeIcon } from "@/Icons/v2/SidebarIcons/Home";
import { InfrastructureIcon } from "@/Icons/v2/SidebarIcons/Infrastructure";
import { LeaderboardIcon } from "@/Icons/v2/SidebarIcons/Leaderboard";
import { LibraryIcon } from "@/Icons/v2/SidebarIcons/Library";
import { PortfolioIcon } from "@/Icons/v2/SidebarIcons/Portfolio";
import { ReportingIcon } from "@/Icons/v2/SidebarIcons/Reporting";
import { TenantsIcon } from "@/Icons/v2/SidebarIcons/Tenants";
import { useTerminalsState } from "@/States/Terminals";
import { useConfig } from "@/api/hooks/useConfig";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import React from "react";

import {
  NavHeaderTypeEnum,
  type NavItemInterface,
  type NavSectionInterface,
  type navigationInterface,
} from "../Sidebar.interface";
import { NavItem } from "./NavItem";

export const getNavigationInterface = ({
  showPortfolio = false,
  isRealEstate = false,
  useExternalCRM = false,
  showContamination = false,
}: {
  showPortfolio: boolean;
  isRealEstate: boolean;
  useExternalCRM: boolean;
  showContamination: boolean;
}): navigationInterface => {
  return {
    [NavHeaderTypeEnum.Overview]: {
      links: [
        {
          to: "/",
          name: "sidebarLabels:home",
          icon: <HomeIcon />,
        },
        showPortfolio && {
          to: "/portfolio",
          name: "sidebarLabels:portfolio",
          icon: <PortfolioIcon />,
        },
        isRealEstate && {
          to: "/reporting",
          name: "sidebarLabels:reporting",
          icon: <ReportingIcon />,
        },
        {
          to: "/events",
          name: "sidebarLabels:events",
          icon: <EventsIcon />,
        },
        showContamination && {
          to: "/contamination",
          name: "sidebarLabels:contamination",
          icon: <ContaminationIcon />,
        },
      ].filter((a) => !!a) as NavItemInterface[],
    },
    [NavHeaderTypeEnum.Customers]: {
      links: [
        isRealEstate && {
          to: "/tenant/dashboard",
          name: "sidebarLabels:tenantFollowUp",
          icon: <LeaderboardIcon />,
        },
        isRealEstate && {
          to: "/tenant/details",
          name: "drawerLabels:tenantDetails",
          icon: <DrilldownIcon />,
        },
        isRealEstate && {
          to: "/tenant/waste-streams",
          name: "statisticsLabels:tenantWasteTypes",
          icon: <ActivityIcon />,
        },
        !isRealEstate &&
          useExternalCRM && {
            to: "/allocations",
            name: "sidebarLabels:allocations",
            icon: <ActivityIcon />,
          },
        isRealEstate && {
          to: "/library",
          name: "sidebarLabels:library",
          icon: <LibraryIcon />,
        },
      ].filter((a) => !!a) as NavItemInterface[],
    },
    [NavHeaderTypeEnum.Setup]: {
      links: [
        {
          to: "/infrastructure/manage",
          name: "sidebarLabels:manageInfrastructure",
          icon: <InfrastructureIcon />,
        },
        {
          to: "/customer/manage",
          name: "sidebarLabels:manageCustomers",
          icon: <TenantsIcon />,
        },
      ].filter((a) => !!a) as NavItemInterface[],
    },
  };
};

export const NavSection: React.FC<NavSectionInterface> = ({
  type,
  collapsedSidebar = false,
}) => {
  const { t } = useTrans();
  const { isRealEstate, config } = useConfig();
  const { terminals } = useTerminalsState();
  const { terminalConfig } = useConfigService();

  const showPortfolio = terminals.length > 1 && isRealEstate;

  const showContamination = !!terminalConfig?.enableContaminationRegistration;

  return (
    <li className="mb-[16.5px]">
      <ul>
        {collapsedSidebar ? (
          <div className="h-[18px]" />
        ) : (
          <li className="font-dm-sans text-forest mb-[6px] px-[25px] text-[10px] font-bold tracking-[.09em] uppercase">
            {t(`sidebarLabels:${type}`)}
          </li>
        )}
        {getNavigationInterface({
          showPortfolio,
          isRealEstate,
          useExternalCRM: !!config?.useExternalCRM,
          showContamination,
        })[type].links.map(({ to, name, icon }, index) => (
          <li key={index}>
            <NavItem
              to={to}
              name={name}
              icon={icon}
              collapsedSidebar={collapsedSidebar}
            />
          </li>
        ))}
      </ul>
    </li>
  );
};
